import React from "react";
import { Box, Collapse, Drawer, IconButton, useTheme } from "@mui/material";
import {
  CheckCircleOutline,
  Close,
  ErrorOutline,
  ExpandLess,
  ExpandMore,
  FactCheckOutlined,
  Inventory2Outlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { observer } from "mobx-react";
import { getPodOrderRowKey, PodOrder } from "../../../models/PodOrder/PodOrder";
import { useStores } from "../../../use-stores";
import AppButton from "../common/buttons/AppButton";
import CustomTypography from "../common/form/CustomTypography";
import FormTextField from "../common/form/FormTextField";
import ImageUpload from "../common/upload/ImageUpload";
import AddEditMockups from "../../pages/orders/AddEditMockups";
import { PodOrderPrintingTypeBadge } from "./podOrderBadges";

const DRAWER_Z_OFFSET = 2000;

export interface PodOrderPrintInwardDrawerProps {
  open: boolean;
  onClose: () => void;
  submitting?: boolean;
  onConfirm: () => void;
}

const PodOrderPrintInwardDrawer: React.FC<PodOrderPrintInwardDrawerProps> = observer(
  ({ open, onClose, submitting = false, onConfirm }) => {
    const theme = useTheme();
    const { podOrderStore } = useStores();

    const lines = [...podOrderStore.printInwardLines].sort(
      (a, b) =>
        a.order_id - b.order_id ||
        a.product_id - b.product_id ||
        (a.stock_issue_request_id ?? 0) - (b.stock_issue_request_id ?? 0),
    );
    const totalQuantity = lines.reduce((sum, line) => sum + line.ordered_qty, 0);
    const receivedTotal = lines.reduce((sum, line) => {
      const lineItem = podOrderStore.getPrintInwardLineItem(line);
      return sum + (lineItem?.quantity ?? 0);
    }, 0);
    const rejectedTotal = lines.reduce((sum, line) => {
      const lineItem = podOrderStore.getPrintInwardLineItem(line);
      return sum + (lineItem?.rejected_quantity ?? 0);
    }, 0);

    const handleConfirm = () => {
      if (!podOrderStore.validatePrintInwardForm()) return;
      onConfirm();
    };


    const renderLineCard = (line: PodOrder) => {
      const rowKey = getPodOrderRowKey(line);
      const lineItem = podOrderStore.getPrintInwardLineItem(line);
      const isExpanded = podOrderStore.printInwardExpandedKey === rowKey;
      const productName = line.product_name?.trim() || `Product #${line.product_id}`;
      const variant = line.variant_name?.trim() || line.sku?.trim() || "—";
      const imageUrl = line.image_url?.trim();

      return (
        <Box
          key={rowKey}
          sx={{
            border: "1px solid",
            borderColor: theme.palette.customColors.lightGray,
            borderRadius: "12px",
            backgroundColor: theme.palette.customColors.white,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "flex-start",
              gap: 1.25,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "10px",
                border: "1px solid",
                borderColor: theme.palette.customColors.lightGray,
                backgroundColor: theme.palette.customColors.lightGreyColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                overflow: "hidden",
              }}
            >
              {imageUrl ? (
                <Box
                  component="img"
                  src={imageUrl}
                  alt={productName}
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <Inventory2Outlined sx={{ fontSize: 20, color: theme.palette.customColors.mediumGray }} />
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <CustomTypography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.45 }}>
                  {productName}
                </CustomTypography>
                <PodOrderPrintingTypeBadge name={line.printing_type} />
              </Box>
              <CustomTypography
                variant="body3"
                color="text.secondary"
                sx={{
                  mt: 0.5,
                  lineHeight: 1.4,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <Box
                  component="span"
                  sx={{ color: theme.palette.customColors.purpleMain, fontWeight: 600 }}
                >
                  ORD-{line.order_id}
                </Box>
                <Box
                  component="span"
                  sx={{ color: theme.palette.customColors.mediumGray, mx: 0.75 }}
                >
                  •
                </Box>
                {variant}
              </CustomTypography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}>
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    py: 0.35,
                    borderRadius: "6px",
                    fontSize: "0.72rem",
                    lineHeight: 1.3,
                    backgroundColor: theme.palette.customColors.lightGreyColor,
                  }}
                >
                  <Box component="span" sx={{ color: theme.palette.customColors.grayText, fontWeight: 500 }}>
                    Order qty
                  </Box>
                  <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 700 }}>
                    {line.ordered_qty}
                  </Box>
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    py: 0.35,
                    borderRadius: "6px",
                    fontSize: "0.72rem",
                    lineHeight: 1.3,
                    backgroundColor: theme.palette.customColors.lightGreyColor,
                  }}
                >
                  <Box component="span" sx={{ color: theme.palette.customColors.grayText, fontWeight: 500 }}>
                    Inward qty
                  </Box>
                  <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 700 }}>
                    {line.total_print_inwarded_qty?.trim() || "0"}
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
              <AppButton
                variant="grayscale"
                label="Mockup"
                fontSize="0.72rem"
                startIcon={<VisibilityOutlined sx={{ fontSize: 16 }} />}
                onClick={() => podOrderStore.openPrintInwardMockupPreview(line)}
                customStyles={{
                  minHeight: 28,
                  height: 28,
                  py: 0,
                  px: 1.5,
                }}
              />
              <IconButton
                size="small"
                aria-label={isExpanded ? "Collapse" : "Expand"}
                onClick={() => podOrderStore.togglePrintInwardExpandedKey(rowKey)}
                sx={{
                  p: 0.25,
                  color: theme.palette.customColors.mediumGray,
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: theme.palette.customColors.purpleMain,
                  },
                }}
              >
                {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
            </Box>
          </Box>

          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1.5,
                }}
              >
                <Box>
                  <CustomTypography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
                    Qty Received
                  </CustomTypography>
                  <FormTextField
                    id={`pod-print-inward-received-${rowKey}`}
                    type="text"
                    value={String(
                      lineItem?.quantity ??
                      line.ordered_qty - Number(line.total_print_inwarded_qty ?? 0),
                    )}
                    marginBottom={0}
                    onChange={(_, value) => {
                      const raw = value ?? "";
                      if (raw !== "" && !/^\d+$/.test(raw)) return;
                      podOrderStore.updatePrintInwardLine(line, {
                        quantity: raw === "" ? 0 : Math.max(0, parseInt(raw, 10) || 0),
                      });
                    }}
                    sx={{
                      mb: 0,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: theme.palette.customColors.white,
                      },
                      "& .MuiOutlinedInput-input": {
                        color: theme.palette.customColors.purpleMain,
                        fontWeight: 700,
                      },
                    }}
                  />
                </Box>
                <Box>
                  <CustomTypography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
                    Qty Rejected
                  </CustomTypography>
                  <FormTextField
                    id={`pod-print-inward-rejected-${rowKey}`}
                    type="text"
                    value={String(lineItem?.rejected_quantity ?? 0)}
                    marginBottom={0}
                    onChange={(_, value) => {
                      const raw = value ?? "";
                      if (raw !== "" && !/^\d+$/.test(raw)) return;
                      podOrderStore.updatePrintInwardLine(line, {
                        rejected_quantity: raw === "" ? 0 : Math.max(0, parseInt(raw, 10) || 0),
                      });
                    }}
                    sx={{
                      mb: 0,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: theme.palette.customColors.white,
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ mt: 1.5 }}>
                <CustomTypography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
                  Rejection Notes
                  {(lineItem?.rejected_quantity ?? 0) > 0 ? " *" : ""}
                </CustomTypography>
                <FormTextField
                  id={`pod-print-inward-notes-${rowKey}`}
                  placeholder="Describe the quality issue..."
                  multiline
                  rows={3}
                  value={lineItem?.rejection_reason ?? ""}
                  marginBottom={0}
                  error={podOrderStore.hasPrintInwardRejectionNotesError(line)}
                  errorMessage="Please add rejection notes"
                  onChange={(_, value) =>
                    podOrderStore.setPrintInwardRejectionNotes(line, value ?? "")
                  }
                  sx={{
                    mb: 0,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      backgroundColor: theme.palette.customColors.white,
                    },
                  }}
                />
              </Box>

              {/* <Box sx={{ mt: 1.5 }}>
                <CustomTypography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
                  Rejection Images
                </CustomTypography>
                <ImageUpload
                  id={`pod-print-inward-images-${rowKey}`}
                  size="large"
                  multiple
                  showImagesList
                  uploadText="Click or drag images here"
                  newImageList={podOrderStore.getPrintInwardRejectionFiles(line)}
                  addImagesToBeUploaded={files =>
                    podOrderStore.setPrintInwardRejectionImages(line, files)
                  }
                  removeImage={image => {
                    const imageIndex = podOrderStore
                      .getPrintInwardRejectionFiles(line)
                      .findIndex(file => file.name === image.title);
                    if (imageIndex !== undefined && imageIndex >= 0) {
                      podOrderStore.removePrintInwardRejectionImage(line, imageIndex);
                    }
                  }}
                  minHeight="120px"
                  minWidth="100%"
                  minInputWidth="100%"
                  maxWidth="100%"
                />
              </Box> */}
            </Box>
          </Collapse>
        </Box>
      );
    };

    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={submitting ? undefined : onClose}
        ModalProps={{
          keepMounted: true,
          disableEnforceFocus: true,
          disableRestoreFocus: true,
          sx: { zIndex: t => t.zIndex.drawer + DRAWER_Z_OFFSET },
          slotProps: {
            backdrop: { sx: { zIndex: t => t.zIndex.drawer + DRAWER_Z_OFFSET - 1 } },
          },
        }}
        PaperProps={{
          sx: {
            width: { xl: 580, lg: 540, md: 520, sm: "100vw", xs: "100vw" },
            maxWidth: "100vw",
            display: "flex",
            flexDirection: "column",
            zIndex: t => t.zIndex.drawer + DRAWER_Z_OFFSET,
            boxShadow: "-4px 0 24px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            position: "relative",
          }}
        >
          <IconButton
            onClick={onClose}
            disabled={submitting}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              backgroundColor: theme.palette.grey[100],
              "&:hover": { backgroundColor: theme.palette.grey[200] },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, pr: 5 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                backgroundColor: theme.palette.customColors.successGreen,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <FactCheckOutlined
                sx={{ color: theme.palette.primary.contrastText, fontSize: 22 }}
              />
            </Box>
            <Box>
              <CustomTypography variant="heading5">Print Inward</CustomTypography>
              <CustomTypography variant="body2" color="text.secondary">
                Record inward receipt for {lines.length} selected {lines.length === 1 ? "item" : "items"}
              </CustomTypography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2.5 }}>
          {podOrderStore.printInwardError ? (
            <Box
              role="alert"
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                mb: 1.5,
                px: 1.5,
                py: 1.25,
                borderRadius: "10px",
                border: "1px solid",
                borderColor: theme.palette.customColors.errorRed,
                backgroundColor: `${theme.palette.error.main}10`,
              }}
            >
              <ErrorOutline
                sx={{
                  fontSize: 20,
                  color: theme.palette.customColors.errorRed,
                  flexShrink: 0,
                  mt: 0.1,
                }}
              />
              <CustomTypography
                variant="body2"
                sx={{ color: theme.palette.customColors.errorRed, lineHeight: 1.45 }}
              >
                {podOrderStore.printInwardError}
              </CustomTypography>
            </Box>
          ) : null}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.25,
            }}
          >
            <CustomTypography variant="heading7">Selected Products</CustomTypography>
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                px: 1.25,
                py: 0.35,
                borderRadius: "999px",
                fontSize: "0.72rem",
                fontWeight: 700,
                backgroundColor: theme.palette.customColors.lightPurple,
                color: theme.palette.customColors.purpleMain,
              }}
            >
              {lines.length} Selected
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {lines.map(line => renderLineCard(line))}
          </Box>
        </Box>

        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            <Box>
              <CustomTypography
                variant="body3"
                color="text.secondary"
                sx={{ letterSpacing: "0.06em", fontWeight: 700 }}
              >
                RECEIVED
              </CustomTypography>
              <CustomTypography variant="heading6" color={theme.palette.customColors.successGreen}>
                {receivedTotal} / {totalQuantity}
              </CustomTypography>
            </Box>
            <Box>
              <CustomTypography
                variant="body3"
                color="text.secondary"
                sx={{ letterSpacing: "0.06em", fontWeight: 700 }}
              >
                REJECTED
              </CustomTypography>
              <CustomTypography variant="heading6" color={theme.palette.customColors.info}>
                {rejectedTotal} / {totalQuantity}
              </CustomTypography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <AppButton
              variant="secondary"
              label="Cancel"
              disabled={submitting}
              onClick={onClose}
              customStyles={{ minWidth: 120 }}
            />
            <AppButton
              variant="primary"
              label={submitting ? "Updating…" : "Confirm Inward"}
              disabled={submitting}
              startIcon={<CheckCircleOutline />}
              onClick={handleConfirm}
              customStyles={{ minWidth: 160 }}
            />
          </Box>
        </Box>

        {podOrderStore.printInwardMockupPreviewOpen &&
          podOrderStore.printInwardMockupPreviewProduct && (
            <AddEditMockups
              viewOnly
              dialogZIndex={theme.zIndex.drawer + DRAWER_Z_OFFSET + 500}
              toggleMockupsPopup={() => podOrderStore.closePrintInwardMockupPreview()}
              product={podOrderStore.printInwardMockupPreviewProduct}
              mockups={podOrderStore.printInwardMockupPreviewMockups}
              isMainMockupImageLoading={podOrderStore.printInwardMockupPreviewLoading}
              canDeleteMockup={false}
              archived={1}
            />
          )}
      </Drawer>
    );
  },
);

export default PodOrderPrintInwardDrawer;
